/**
 * Contrato da automação que transforma um pedido comprado em despesa do caixa.
 *
 * Papel: aqui ficam `viraComprado`, que reconhece a transição que dispara a
 * automação; `posicoesSemCategoria`, que diz que itens ainda impedem essa
 * transição; `despesasDoPedido`, que monta as despesas de cada item;
 * `formaPagamentoDoLancamento` e o caminho inverso, `formaPagamentoDoPedido`;
 * `classificacaoDoFornecedor`, que traz o perfil de um favorecido para o item, e
 * o `classificacaoFinanceiraSchema` de onde esse perfil vem; `casaDoCaixa`, que
 * diz em que casa cai a despesa do pedido sem casa; e os dois valores que o
 * lançamento gerado traz — `CATEGORIA_A_CLASSIFICAR` e `OBSERVACAO_DA_AUTOMACAO`.
 *
 * Como usar: `despesasDoPedido` é **pura e não conhece o banco**. Ela devolve
 * `fornecedor` como o nome que estava no item, e não o `id` do favorecido:
 * casar o nome com o cadastro — e criar quem não existe — é de
 * `favorecidoPorNome`, na API, no mesmo lote da gravação.
 *
 * A despesa é gravada como o modal grava um lançamento: o bruto é reconstruído
 * do valor final do item e vai com o desconto e o acréscimo dele, e cada parcela
 * tem o **valor cheio** do item, com a descrição terminada em " (i/N)". Quem
 * somar as parcelas de um item obtém o valor dele vezes o número de parcelas.
 *
 * Este arquivo mora fora de `compras.ts` e de `financas.ts` de propósito: a regra
 * é a única que pertence aos dois módulos, e escondê-la dentro de um deles faria
 * parecer que o outro apenas a sofre.
 */
import { z } from 'zod';

import {
  type FormaPagamentoPedido,
  type StatusFinanceiroPedido,
  type StatusPedidoCompra,
  valorFinalDoItem,
} from './compras';
import {
  type FormaPagamentoLancamento,
  type IntervaloParcelamento,
  datasDasParcelas,
  formaPagamentoLancamentoSchema,
} from './financas';
import { arredondarCentavos } from './formatos';

/**
 * A categoria que o item sem classificação recebe em `despesasDoPedido`.
 *
 * É rede de segurança, e não caminho de uso: a rota que grava o pedido recusa a
 * virada para `comprado` enquanto `posicoesSemCategoria` apontar algum item. Se
 * uma despesa chegar aqui sem categoria mesmo assim, ela entra no caixa com esta,
 * e a pendência fica visível no caixa e no plano de contas, onde a gravação a
 * acrescenta como categoria usada.
 */
export const CATEGORIA_A_CLASSIFICAR = 'A classificar';

/** O que o lançamento gerado traz em `observacao`, para se distinguir do digitado. */
export const OBSERVACAO_DA_AUTOMACAO = 'Gerado automaticamente a partir do pedido de compra.';

/** Até onde a descrição do item cabe na do lançamento, antes do sufixo da parcela. */
export const LIMITE_DA_DESCRICAO = 180;

/**
 * O que a API responde, e o formulário mostra, quando a virada para `comprado`
 * encontra item sem categoria financeira.
 */
export const AVISO_DE_CATEGORIA_PENDENTE =
  'Selecione a categoria financeira de cada item antes de marcar como Comprado.';

/**
 * As posições dos itens que ainda não têm categoria financeira.
 *
 * A virada para `comprado` é recusada enquanto esta lista não estiver vazia: a
 * despesa de cada item nasce com a categoria dele, e o item sem ela não teria
 * onde entrar no caixa. Quem chama é a rota que grava o pedido, o formulário,
 * que marca o campo de cada posição devolvida, e a caixa de compra do quadro.
 *
 * @param itens - Os itens do pedido, na ordem do formulário; a categoria pode
 *   chegar nula, ausente, vazia ou só com espaços, e as quatro contam como falta.
 * @returns As posições, contadas do zero; vazia no pedido sem item.
 */
export function posicoesSemCategoria(
  itens: readonly { categoriaFinanceira?: string | null }[],
): number[] {
  return itens.flatMap((item, posicao) =>
    (item.categoriaFinanceira ?? '').trim() === '' ? [posicao] : [],
  );
}

/**
 * Traduz a forma de pagamento padrão de um favorecido para a do item do pedido.
 *
 * É o caminho inverso de `formaPagamentoDoLancamento`, e só as quatro formas que
 * os dois lados têm atravessam. Transferência, débito automático e cartão de
 * débito não existem no pedido, e viram `null` — o formulário então deixa a
 * forma do item como estava.
 *
 * @param forma - A forma padrão do favorecido, ou `null`.
 * @returns A forma correspondente no pedido, ou `null` quando não há uma.
 */
export function formaPagamentoDoPedido(
  forma: FormaPagamentoLancamento | null,
): FormaPagamentoPedido | null {
  if (
    forma === 'pix' ||
    forma === 'boleto' ||
    forma === 'cartao_de_credito' ||
    forma === 'dinheiro'
  ) {
    return forma;
  }
  return null;
}

/** O perfil de um nome conhecido do caixa, como a rota da classificação o devolve. */
export const perfilDoFornecedorSchema = z.object({
  nome: z.string(),
  categoriaPadrao: z.string().nullable(),
  subcategoriaPadrao: z.string().nullable(),
  formaPagamentoPadrao: formaPagamentoLancamentoSchema.nullable(),
});

export type PerfilDoFornecedor = z.infer<typeof perfilDoFornecedorSchema>;

/**
 * A resposta de `GET /compras/classificacao-financeira`.
 *
 * `categorias` são as de natureza `despesa` do plano de contas, em ordem
 * alfabética, cada uma com as subcategorias também em ordem — é o que os dois
 * selects do cartão financeiro de um item oferecem. `favorecidos` são os da casa
 * do caixa, em ordem de nome, e entram na sugestão de "Nome do fornecedor";
 * `padroes` são os padrões de favorecido, que só servem ao autopreenchimento.
 */
export const classificacaoFinanceiraSchema = z.object({
  categorias: z.array(z.object({ nome: z.string(), subcategorias: z.array(z.string()) })),
  favorecidos: z.array(perfilDoFornecedorSchema),
  padroes: z.array(perfilDoFornecedorSchema),
});

export type ClassificacaoFinanceira = z.infer<typeof classificacaoFinanceiraSchema>;

/** A classificação que o nome do fornecedor traz para o item. */
export interface ClassificacaoDoItem {
  categoriaFinanceira: string | null;
  subcategoriaFinanceira: string | null;
  formaPagamento: FormaPagamentoPedido | null;
}

/**
 * A classificação que um fornecedor conhecido traz para o item do pedido.
 *
 * O casamento é o de `chaveDoNome` na API: sem diferenciar maiúsculas e sem os
 * espaços das pontas, que é como a automação acha o favorecido da despesa
 * depois. Um nome que casa no formulário e não casasse na gravação faria a
 * despesa ir para um favorecido novo, com a classificação que a tela prometeu
 * mas sem o vínculo com o cadastro.
 *
 * O favorecido cadastrado vence o padrão de mesmo nome; o padrão só vale para o
 * nome que não é favorecido. Entre dois favorecidos que só mudam na caixa, vale
 * o primeiro da lista, que chega em ordem de nome.
 *
 * @param nome - O "Nome do fornecedor" como está no campo.
 * @param favorecidos - Os favorecidos do caixa, em ordem de nome.
 * @param padroes - Os padrões de favorecido.
 * @returns A categoria, a subcategoria e a forma do perfil, cada uma `null`
 *   quando o perfil não a tem; `null` inteiro quando o nome não casa com
 *   ninguém ou está em branco.
 */
export function classificacaoDoFornecedor(
  nome: string,
  favorecidos: readonly PerfilDoFornecedor[],
  padroes: readonly PerfilDoFornecedor[],
): ClassificacaoDoItem | null {
  const chave = nome.trim().toLocaleLowerCase('pt-BR');
  if (chave === '') return null;

  const casa = (perfil: PerfilDoFornecedor) =>
    perfil.nome.trim().toLocaleLowerCase('pt-BR') === chave;
  const perfil = favorecidos.find(casa) ?? padroes.find(casa);
  if (perfil === undefined) return null;

  return {
    categoriaFinanceira: perfil.categoriaPadrao,
    subcategoriaFinanceira: perfil.subcategoriaPadrao,
    formaPagamento: formaPagamentoDoPedido(perfil.formaPagamentoPadrao),
  };
}

/**
 * Diz se a mudança de situação é a que gera despesa.
 *
 * É a **virada** que dispara, e não o estado: salvar de novo um pedido que já
 * estava `comprado` não gera nada.
 *
 * @param anterior - A situação gravada antes desta alteração, ou `null` num
 *   pedido que está sendo criado.
 * @param atual - A situação que o corpo da requisição traz.
 * @returns Verdadeiro apenas quando o pedido acaba de chegar a `comprado`.
 */
export function viraComprado(
  anterior: StatusPedidoCompra | null,
  atual: StatusPedidoCompra,
): boolean {
  return atual === 'comprado' && anterior !== 'comprado';
}

/**
 * Traduz a forma de pagamento do pedido para a do caixa.
 *
 * @param forma - A forma que o item do pedido traz, ou `null`.
 * @returns A forma correspondente no caixa; `outro`, que o caixa não tem, e o
 *   item sem forma viram `null`.
 */
export function formaPagamentoDoLancamento(
  forma: FormaPagamentoPedido | null,
): FormaPagamentoLancamento | null {
  if (
    forma === 'pix' ||
    forma === 'boleto' ||
    forma === 'cartao_de_credito' ||
    forma === 'dinheiro'
  ) {
    return forma;
  }
  return null;
}

/** Um item do pedido, no mínimo que a automação precisa dele. */
export interface ItemQueViraDespesa {
  descricao: string;
  quantidade: number;
  valorUnitario: number;
  desconto: number;
  acrescimo: number;
  fornecedor: string | null;
  categoriaFinanceira: string | null;
  subcategoriaFinanceira: string | null;
  formaPagamento: FormaPagamentoPedido | null;
  statusFinanceiro: StatusFinanceiroPedido;
  dataCompra: string | null;
  parcelas: number | null;
  intervaloParcelamento: IntervaloParcelamento | null;
}

/**
 * Os três indícios de onde está o caixa, cada um a casa de um registro.
 *
 * Cada campo é o `residenciaId` do primeiro registro da listagem correspondente
 * — meses por competência decrescente, favorecidos por nome, casas pelo
 * continente e depois pelo próprio nome — ou `undefined` quando ela está vazia.
 */
export interface IndiciosDoCaixa {
  /** A casa do mês mais recente gravado. */
  doMesMaisRecente: string | undefined;
  /** A casa do primeiro favorecido cadastrado. */
  doPrimeiroFavorecido: string | undefined;
  /** A primeira casa da lista de residências. */
  daPrimeiraCasa: string | undefined;
}

/**
 * Diz de qual casa é o caixa, pelos indícios que os registros deixam.
 *
 * A ordem vai da evidência mais forte para a mais fraca: um mês gravado prova
 * onde o caixa está, um favorecido sugere, e a lista de casas só diz onde um
 * caixa ainda inexistente começaria. Quem chama é `residenciaDoCaixa`, na API,
 * que serve à tela de Finanças e à despesa do pedido sem casa — as duas precisam
 * passar pela mesma função, ou a despesa iria para um caixa que a tela não abre.
 *
 * @param indicios - A casa do primeiro registro de cada listagem.
 * @returns O identificador da casa do caixa, ou `null` quando não há casa.
 */
export function casaDoCaixa(indicios: IndiciosDoCaixa): string | null {
  return (
    indicios.doMesMaisRecente ??
    indicios.doPrimeiroFavorecido ??
    indicios.daPrimeiraCasa ??
    null
  );
}

/**
 * O pedido, no mínimo que a automação precisa dele para montar as despesas.
 *
 * A casa não está aqui: quem grava a põe em cada linha — a do pedido, ou a de
 * `casaDoCaixa` quando o pedido é de `Ambas / não se aplica` —, e a tela que só
 * mostra as despesas não precisa dela.
 */
export interface PedidoQueViraDespesa {
  /** Entra na descrição do item que não tem uma. */
  numero: string;
  /** A data do item que não tem data de compra. */
  data: string;
}

/**
 * Uma despesa a gravar, com o favorecido ainda por nome.
 *
 * `fornecedor` é o texto aparado do item, ou `null` quando ele está em branco —
 * a despesa vai sem favorecido. `itemPosicao` distingue duas despesas que vieram
 * de itens diferentes mas saem iguais: é por ela que quem grava dá um
 * `parcelamentoId` a cada item parcelado.
 */
export interface DespesaDoPedido {
  /** A posição do item que originou esta despesa, contada do zero. */
  itemPosicao: number;
  fornecedor: string | null;
  data: string;
  categoria: string;
  subcategoria: string | null;
  descricao: string;
  formaPagamento: FormaPagamentoLancamento | null;
  valorBruto: number;
  desconto: number;
  acrescimo: number;
  pago: boolean;
  provisao: boolean;
  parcelaNumero: number | null;
  parcelasTotal: number | null;
}

/**
 * Monta as despesas que um pedido comprado gera.
 *
 * Uma despesa por item à vista, e uma por parcela no item parcelado. O bruto é
 * `max(valor final + desconto − acréscimo, 0)`, com o desconto e o acréscimo do
 * item junto, e o valor final do caixa volta a ser o do item. A parcela tem o
 * bruto inteiro; as datas andam pelo intervalo do item, e o `avulso`, que não
 * traz as datas, anda de mês em mês. Pago é tudo o que não está `provisionado`.
 *
 * A casa não entra na despesa montada: quem grava a põe em cada linha, e por
 * isso a tela que só mostra as despesas — a caixa "Lançado no Financeiro" —
 * chama esta mesma função e vê a mesma data, descrição e classificação que vão
 * para o caixa.
 *
 * @param pedido - O pedido que acabou de virar comprado: o número e a data.
 * @param itens - Os itens dele, na ordem em que aparecem.
 * @returns As despesas, na ordem dos itens e das parcelas.
 */
export function despesasDoPedido(
  pedido: PedidoQueViraDespesa,
  itens: readonly ItemQueViraDespesa[],
): DespesaDoPedido[] {
  return itens.flatMap((item, itemPosicao): DespesaDoPedido[] => {
    // A data do pedido é obrigatória: o item sem data de compra herda a dele.
    const primeira = item.dataCompra ?? pedido.data;
    const descricao = (
      item.descricao.trim() === '' ? `Pedido de compra ${pedido.numero}` : item.descricao
    ).slice(0, LIMITE_DA_DESCRICAO);
    const fornecedor = item.fornecedor?.trim() ?? '';
    const subcategoria = item.subcategoriaFinanceira?.trim() ?? '';
    const pago = item.statusFinanceiro !== 'provisionado';

    const comum = {
      itemPosicao,
      fornecedor: fornecedor === '' ? null : fornecedor,
      categoria: item.categoriaFinanceira ?? CATEGORIA_A_CLASSIFICAR,
      subcategoria: subcategoria === '' ? null : subcategoria,
      formaPagamento: formaPagamentoDoLancamento(item.formaPagamento),
      valorBruto: arredondarCentavos(
        Math.max(valorFinalDoItem(item) + item.desconto - item.acrescimo, 0),
      ),
      desconto: item.desconto,
      acrescimo: item.acrescimo,
      pago,
      provisao: !pago,
    };

    const parcelas = item.parcelas;
    if (parcelas === null || parcelas < 2) {
      return [
        { ...comum, data: primeira, descricao, parcelaNumero: null, parcelasTotal: null },
      ];
    }

    const intervalo =
      item.intervaloParcelamento === null || item.intervaloParcelamento === 'avulso'
        ? 'mensal'
        : item.intervaloParcelamento;
    return datasDasParcelas(primeira, parcelas, intervalo).map((data, posicao) => ({
      ...comum,
      data,
      descricao: `${descricao} (${String(posicao + 1)}/${String(parcelas)})`,
      parcelaNumero: posicao + 1,
      parcelasTotal: parcelas,
    }));
  });
}
